// Width of the Chat Console, which the user can drag wider (up to 50%) from its left edge.
//
// The chat OVERLAYS the timeline rather than squeezing it — the canvas always spans the
// full width, and `autoZoom` frames each forecast into the part the panels leave visible.
// So a wider chat changes two things: how much of the timeline is visible, and (on the
// next framing) how a forecast is scaled to fit it.

/** The default (and minimum) width — the chat's width before it became resizable. */
export const CHAT_W_MIN = 264;
/** Up to 50% wider than the default: 264 × 1.5. */
export const CHAT_W_MAX = 396;
/** localStorage key for the user's chosen width. */
export const CHAT_W_STORAGE_KEY = "sooth-chat-width";

/**
 * Clamp a width into [CHAT_W_MIN, CHAT_W_MAX] — and, on a narrow window, to no more than
 * half the viewport, so the timeline keeps room. Non-finite input reads as the minimum.
 *
 * @param {number} w
 * @param {number} [viewportW]
 * @returns {number}
 */
export function clampChatWidth(w, viewportW = Infinity) {
  const max = Math.max(CHAT_W_MIN, Math.min(CHAT_W_MAX, viewportW / 2));
  if (!Number.isFinite(w)) return CHAT_W_MIN;
  return Math.round(Math.min(max, Math.max(CHAT_W_MIN, w)));
}

/**
 * The timeline's new left edge (day-space `viewLeft`) that keeps the day at the chat's
 * left edge PINNED while the chat changes width — so, during a drag, the forecast slides
 * with the edge instead of disappearing under it. Zoom (`ppd`) is unchanged; this is a pan.
 *
 * The edge sits at `canvasW − chatW` px; the day there is `vl + (canvasW − chatW) / ppd`.
 * Holding that equal across the resize gives `vl′ = vl + (newW − oldW) / ppd`, independent
 * of the canvas width.
 *
 * @param {number} vl current viewLeft (days)
 * @param {number} ppd pixels per day
 * @param {number} oldW chat width before
 * @param {number} newW chat width after
 * @returns {number}
 */
export function pinnedViewLeft(vl, ppd, oldW, newW) {
  if (!Number.isFinite(ppd) || ppd <= 0) return vl;
  return vl + (newW - oldW) / ppd;
}
